/**
 * SubjectSelectWithInlineCreate — extracted from LecturesPage's two near-
 * identical Select-with-inline-creator blocks (live recording save dialog +
 * manual create-lecture dialog). Saves ~120 lines of duplication and gives
 * us one place to evolve the UX (e.g. adding emoji/icon picker later).
 *
 * Behavior:
 *   - Renders a shadcn <Select> seeded with `subjects`.
 *   - Adds a "+ Create new subject…" sentinel option at the bottom.
 *   - Clicking the sentinel swaps the Select for an inline mini-form
 *     (name + color picker + Create/Cancel).
 *   - On Create, POSTs to /api/subjects, optimistically prepends to
 *     subjects, calls onSubjectCreated(newSubject), and onChange(newId).
 *
 * Required props:
 *   value, onChange  — same shape as <Select value onValueChange>
 *   subjects         — current cached list (will be reused, not refetched)
 *   onSubjectCreated — (subject) => void; parent updates its cache
 *   testidPrefix     — "save" | "manual" | etc — yields stable testids
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';

const NEW_SUBJECT_SENTINEL = '__create_new__';
const COLORS = ['#002FA7', '#16A34A', '#F59E0B', '#EF4444', '#0EA5E9', '#A855F7'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubjectSelectWithInlineCreate({
  value,
  onChange,
  subjects,
  onSubjectCreated,
  testidPrefix = 'subject-select',
  required = false,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Give the subject a name');
      return;
    }
    setSubmitting(true);
    try {
      const { data: created } = await axios.post(`${API}/subjects`, {
        name: trimmed, color,
      });
      onSubjectCreated?.(created);
      onChange(created.id);
      setIsCreating(false);
      setName('');
      toast.success(`Subject "${created.name}" created`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create subject');
    } finally {
      setSubmitting(false);
    }
  };

  if (isCreating) {
    return (
      <div
        className="space-y-2 p-3 bg-[#002FA7]/5 border border-[#002FA7]/20 rounded-xl"
        data-testid={`${testidPrefix}-inline-creator`}
      >
        <Input
          autoFocus
          placeholder="e.g. Biochemistry 201"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              e.preventDefault();
              handleCreate();
            }
          }}
          className="rounded-lg bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder-[var(--isp-muted)]"
          data-testid={`${testidPrefix}-inline-name`}
        />
        <div className="flex items-center gap-2">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`w-6 h-6 rounded-full transition-all ${color === c ? 'ring-2 ring-offset-2 ring-offset-[var(--isp-glass)] ring-[var(--isp-heading)]' : 'opacity-70 hover:opacity-100'}`}
              style={{ backgroundColor: c }}
              aria-label={`Pick color ${c}`}
              data-testid={`${testidPrefix}-inline-color-${c.replace('#', '')}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleCreate}
            disabled={submitting || !name.trim()}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8 rounded-lg flex-1"
            data-testid={`${testidPrefix}-inline-create-btn`}
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create & select'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setIsCreating(false); setName(''); }}
            className="text-xs h-8 rounded-lg border-[var(--isp-glass-border)] text-[var(--isp-body)]"
            data-testid={`${testidPrefix}-inline-cancel-btn`}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === NEW_SUBJECT_SENTINEL) {
          setIsCreating(true);
          return;
        }
        onChange(v);
      }}
      required={required}
    >
      <SelectTrigger
        className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
        data-testid={`${testidPrefix}-trigger`}
      >
        <SelectValue placeholder="Select subject" />
      </SelectTrigger>
      <SelectContent className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
        {subjects.map((s) => (
          <SelectItem key={s.id} value={s.id} className="text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]">
            {s.name}
          </SelectItem>
        ))}
        {subjects.length > 0 && (
          <div className="my-1 mx-2 h-px bg-[var(--isp-glass-border)]" />
        )}
        <SelectItem
          value={NEW_SUBJECT_SENTINEL}
          className="text-[#002FA7] font-semibold hover:bg-[var(--isp-glass-inner)]"
          data-testid={`${testidPrefix}-create-new`}
        >
          + Create new subject…
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
